import { sortStages } from './enums.js';
const STAGE_BY_TOKEN = new Map([
    ['pre-seed', 'pre_seed'],
    ['pre seed', 'pre_seed'],
    ['preseed', 'pre_seed'],
    ['seed', 'seed'],
    ['a', 'series_a'],
    ['series a', 'series_a'],
    ['b', 'series_b'],
    ['series b', 'series_b'],
    ['c', 'series_c_plus'],
    ['c+', 'series_c_plus'],
    ['series c', 'series_c_plus'],
    ['series c+', 'series_c_plus'],
    // One row. Growth/buyout money arrives at the same point in a company's life
    // as a late venture round, so it lands on the last stage rather than nowhere.
    ['pe', 'series_c_plus'],
]);
/**
 * Reads both sheets' vocabularies — "Seed, A" and "Seed, Series A, Series C+".
 */
export function parseStages(raw) {
    const stages = [];
    const unrecognised = [];
    for (const part of (raw ?? '').split(/[,/]/)) {
        const token = part.trim();
        if (token === '')
            continue;
        const stage = STAGE_BY_TOKEN.get(token.toLowerCase());
        if (stage === undefined) {
            unrecognised.push(token);
        }
        else {
            stages.push(stage);
        }
    }
    return { stages: sortStages(stages), unrecognised };
}
/**
 * Dealroom's round vocabulary against our five stages, for the cross-check the
 * grid shows beside a claimed stage: "the rounds we hold say Series A and B".
 *
 * Only a venture round names a stage. An acquisition, a grant, a debt facility,
 * an IPO, a buyout or a secondary is money arriving for a different reason, and
 * reading a stage out of one would say a fund was seen at a stage it never
 * invested at. Those answer null, which is what keeps the hint to VC rounds
 * without a second list to hold in step.
 *
 * The terms are the 42 `standardised_round_label` values read off the live
 * `/transactions/filters` answer (recorded under `apps/fake-dealroom/fixtures/`),
 * plus the pre-seed and extension spellings other sources write.
 */
const STAGE_BY_ROUND_LABEL = new Map([
    ['PRE SEED', 'pre_seed'],
    ['MICRO SEED', 'pre_seed'],
    ['SEED', 'seed'],
    ['SEED+', 'seed'],
    ['SEED PLUS', 'seed'],
    ['SERIES A', 'series_a'],
    ['SERIES B', 'series_b'],
    // One bucket from Series C on, because `Stage` has no finer word for them.
    ['SERIES C', 'series_c_plus'],
    ['SERIES D', 'series_c_plus'],
    ['SERIES E', 'series_c_plus'],
    ['SERIES F', 'series_c_plus'],
    ['SERIES G', 'series_c_plus'],
    ['SERIES H', 'series_c_plus'],
    ['SERIES I', 'series_c_plus'],
]);
/**
 * An extension is the same round again, so it is the same stage. Stripping the
 * suffix here is one rule instead of a second entry for every series above.
 */
export function normaliseRoundLabel(label) {
    return label
        .toUpperCase()
        .replaceAll(/[-_]/g, ' ')
        .replaceAll(/\s+/g, ' ')
        .trim()
        .replace(/ EXTENSION$/, '');
}
/** Null for a label that names no venture stage, which is most of them. */
export function stageOfRoundLabel(label) {
    if (label === null)
        return null;
    return STAGE_BY_ROUND_LABEL.get(normaliseRoundLabel(label)) ?? null;
}
/**
 * Round sizes in EUR millions that read a seed-flavoured round as a stage:
 * (0, 3] pre-seed, (3, 8] seed, (8, 15] Series A. Lower bounds exclusive, upper
 * inclusive. Over 15 the label stands.
 */
const SIZE_BANDS = [
    { upTo: 3, stage: 'pre_seed' },
    { upTo: 8, stage: 'seed' },
    { upTo: 15, stage: 'series_a' },
];
/**
 * The labels Dealroom files small early rounds under without agreeing on which
 * stage they are; their size says. A round explicitly labelled Series A or
 * later keeps its label whatever it raised.
 */
const SIZE_DECIDED_LABELS = new Set([
    'PRE SEED',
    'MICRO SEED',
    'SEED',
    'SEED+',
    'SEED PLUS',
    'EARLY VC',
]);
/**
 * The stage a round counts as: its label's, except that a seed-flavoured label
 * takes its stage from the round size when there is one up to 15M. No amount
 * (null or 0) keeps the label's stage. `round_stage()` in schema.sql says the
 * same in SQL; a test holds the two in step.
 */
export function stageOfRound(round) {
    const { label, amountEurMillions } = round;
    if (label !== null &&
        SIZE_DECIDED_LABELS.has(normaliseRoundLabel(label)) &&
        amountEurMillions !== null &&
        amountEurMillions > 0) {
        const band = SIZE_BANDS.find(({ upTo }) => amountEurMillions <= upTo);
        if (band !== undefined)
            return band.stage;
    }
    return stageOfRoundLabel(label);
}
/**
 * Two labels name the same round when they read the same once case, hyphens
 * and an extension are set aside: "Pre-seed" is Dealroom's "PRE SEED".
 */
export function sameRoundLabel(left, right) {
    if (left === null || right === null)
        return false;
    const key = normaliseRoundLabel(left);
    return key !== '' && key === normaliseRoundLabel(right);
}
//# sourceMappingURL=stages.js.map